<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import MetaLeadFormsAPI from 'dashboard/api/metaLeadForms';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FormConfig from './FormConfig.vue';
import LeadsPanel from './LeadsPanel.vue';

const store = useStore();

const loading = ref(true);
const forms = ref([]);
const metrics = ref([]);
const loadingMetrics = ref(false);
const period = ref('30');
const formRef = ref(null);

const inboxesList = computed(() => store.getters['inboxes/getInboxes'] || []);

const inboxName = id => {
  const inbox = inboxesList.value.find(i => i.id === id);
  return inbox?.name || `Inbox #${id}`;
};

const aiOn = form =>
  form?.capture_config?.ai_continue === true ||
  form?.capture_config?.ai_continue === 'true';

const mappingCount = form => {
  const fm = form?.capture_config?.field_mapping;
  return fm && typeof fm === 'object' ? Object.keys(fm).length : 0;
};

const loadForms = async () => {
  try {
    const { data } = await MetaLeadFormsAPI.get();
    forms.value = Array.isArray(data) ? data : [];
  } catch (e) {
    forms.value = [];
  }
};

const loadMetrics = async () => {
  loadingMetrics.value = true;
  try {
    const since = new Date(
      Date.now() - Number(period.value) * 24 * 60 * 60 * 1000
    ).toISOString();
    const { data } = await MetaLeadFormsAPI.metrics({ since });
    metrics.value = Array.isArray(data?.by_form) ? data.by_form : [];
  } catch (e) {
    metrics.value = [];
  } finally {
    loadingMetrics.value = false;
  }
};

const loadAll = async () => {
  loading.value = true;
  try {
    if (!inboxesList.value.length) await store.dispatch('inboxes/get');
    await Promise.all([loadForms(), loadMetrics()]);
  } finally {
    loading.value = false;
  }
};
onMounted(loadAll);

const formName = form =>
  forms.value.find(f => f.capture_config?.meta_form_id === form.form_id)?.name ||
  form.form_id ||
  '—';

const newForm = () => formRef.value?.open(null);
const editForm = f => formRef.value?.open(f);

const deleteForm = async f => {
  // eslint-disable-next-line no-alert
  if (!window.confirm('Excluir esta configuração de formulário?')) return;
  try {
    await MetaLeadFormsAPI.delete(f.id);
    await Promise.all([loadForms(), loadMetrics()]);
  } catch (e) {
    useAlert('Não foi possível excluir.');
  }
};

const onSaved = async () => {
  await Promise.all([loadForms(), loadMetrics()]);
};
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto">
    <div class="w-full max-w-[60rem] mx-auto p-6 flex flex-col gap-6">
      <header class="flex flex-col gap-1">
        <h1 class="text-xl font-extrabold text-n-slate-12">
          Captura de Leads — Formulários Meta
        </h1>
        <p class="text-sm text-n-slate-11">
          Receba os leads dos formulários do Meta, salve as respostas em campos
          personalizados, mande uma mensagem e (opcional) deixe a IA continuar a
          conversa.
        </p>
      </header>

      <div v-if="loading" class="flex justify-center py-10 text-n-slate-11">
        <Spinner />
      </div>

      <template v-else>
        <!-- A configuração do app Meta (webhooks + app secret + verify token) é a
             MESMA dos comentários IG/FB → fica num lugar só, em Integrações → Meta. -->
        <p class="text-xs text-n-slate-11 p-3 rounded-xl bg-n-alpha-2 border border-n-weak">
          <span class="i-ri-information-line size-3.5 align-text-bottom" />
          A configuração do app Meta (webhook do campo <code>leadgen</code> + app secret
          + verify token) fica em <strong>Configurações → Integrações → Meta</strong> —
          é a mesma dos comentários de Instagram/Facebook, configurada uma vez só.
        </p>

        <!-- Formulários configurados -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-n-slate-12">
              Formulários configurados
            </h2>
            <Button
              label="Novo formulário"
              size="sm"
              icon="i-ri-add-line"
              @click="newForm"
            />
          </div>
          <div v-if="forms.length" class="flex flex-col gap-2">
            <div
              v-for="f in forms"
              :key="f.id"
              class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak"
            >
              <div class="flex flex-col gap-0.5 min-w-0">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ f.name || 'Formulário' }}
                </span>
                <span class="text-xs text-n-slate-11 truncate">
                  {{ inboxName(f.inbox_id) }} ·
                  {{ aiOn(f) ? 'IA continua' : 'só mensagem' }} ·
                  {{ mappingCount(f) }} campo(s) ·
                  form {{ f.capture_config?.meta_form_id || '— (qualquer)' }}
                </span>
              </div>
              <div class="flex items-center gap-1 shrink-0">
                <Button
                  variant="faded"
                  color="slate"
                  size="xs"
                  icon="i-ri-pencil-line"
                  @click="editForm(f)"
                />
                <Button
                  variant="faded"
                  color="ruby"
                  size="xs"
                  icon="i-ri-delete-bin-line"
                  @click="deleteForm(f)"
                />
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            Nenhum formulário configurado ainda. Crie o primeiro acima.
          </p>
        </section>

        <!-- Leads recebidos (dashboard por formulário) -->
        <LeadsPanel :forms="forms" />

        <!-- Métricas geral (todos os formulários) -->
        <section class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h2 class="text-sm font-semibold text-n-slate-12">Métricas</h2>
            <select
              v-model="period"
              class="w-40 shrink-0 px-2 py-1 rounded-md border border-n-weak bg-n-background text-n-slate-12 text-xs"
              @change="loadMetrics"
            >
              <option value="7">Últimos 7 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="90">Últimos 90 dias</option>
            </select>
          </div>
          <div
            v-if="loadingMetrics"
            class="flex justify-center py-4 text-n-slate-11"
          >
            <Spinner />
          </div>
          <div
            v-else-if="metrics.length"
            class="rounded-xl border border-n-weak overflow-hidden"
          >
            <table class="w-full text-sm">
              <thead class="bg-n-alpha-1 text-n-slate-11">
                <tr>
                  <th class="text-left font-medium px-3 py-2">Formulário</th>
                  <th class="text-right font-medium px-3 py-2">Recebidos</th>
                  <th class="text-right font-medium px-3 py-2">Responderam</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="m in metrics"
                  :key="m.form_id"
                  class="border-t border-n-weak"
                >
                  <td class="px-3 py-2 text-n-slate-12">{{ formName(m) }}</td>
                  <td class="px-3 py-2 text-right text-n-slate-12">
                    {{ m.recebidos }}
                  </td>
                  <td class="px-3 py-2 text-right text-n-slate-12">
                    {{ m.responderam }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="text-xs text-n-slate-10">
            Nenhum lead recebido no período.
          </p>
        </section>
      </template>
    </div>

    <FormConfig ref="formRef" :inboxes="inboxesList" :forms="forms" @saved="onSaved" />
  </div>
</template>
